import React from "react";
import PropTypes from "prop-types";
import { IconButton, PlusIcon, SecondaryButton, TrashIcon } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import ConditionPicker from "./ConditionPicker";
import { emptyGuard } from "./automationUtils";

/**
 * Add / remove guard rows. Each guard is {key, params}; all guards are ANDed
 * and checked per user right before a message is enqueued.
 */
const GuardsEditor = ({
  guards = [],
  conditions = [],
  automationSlugs = [],
  extraSlugOptions = [],
  onChange,
  disabled = false,
  errors = {},
}) => {
  const updateGuard = (index, patch) => {
    onChange(guards.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  };

  const removeGuard = (index) => {
    onChange(guards.filter((_, i) => i !== index));
  };

  const addGuard = () => onChange([...guards, emptyGuard()]);

  const paramErrorsFor = (index) => {
    const prefix = `guards.${index}.params.`;
    const out = {};
    Object.entries(errors || {}).forEach(([field, message]) => {
      if (field.startsWith(prefix)) out[field.slice(prefix.length)] = message;
    });
    return out;
  };

  return (
    <div className="space-y-3">
      {guards.length === 0 ? (
        <div className={`rounded-2xl border border-dashed ${borders.default} ${surfaces.secondary} px-4 py-5 text-center`}>
          <p className={`${typography.bodySmall} ${textColors.secondary}`}>
            No guards — every user who enters the situation qualifies.
          </p>
          <p className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>
            Add guards to require extra per-user conditions ("only if…"). All guards must pass.
          </p>
        </div>
      ) : (
        <ol className="space-y-3">
          {guards.map((guard, index) => (
            <li
              key={guard._key || index}
              className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}
              aria-label={`Guard ${index + 1}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className={`${typography.label} ${textColors.primary}`}>Guard {index + 1}</div>
                  <div className={`${typography.caption} ${textColors.tertiary}`}>
                    {index === 0 ? "Only if…" : "…and only if…"}
                  </div>
                </div>
                <IconButton
                  size="sm"
                  variant="ghost"
                  shape="square"
                  icon={<TrashIcon size="sm" />}
                  aria-label={`Remove guard ${index + 1}`}
                  title="Remove guard"
                  onClick={() => removeGuard(index)}
                  disabled={disabled}
                />
              </div>
              <ConditionPicker
                id={`guard-${guard._key || index}`}
                label="Guard condition"
                conditions={conditions}
                value={guard.key || ""}
                params={guard.params || {}}
                onChange={(key, params) => updateGuard(index, { key, params })}
                onParamChange={(name, value) =>
                  updateGuard(index, { params: { ...(guard.params || {}), [name]: value } })
                }
                automationSlugs={automationSlugs}
                extraSlugOptions={extraSlugOptions}
                placeholder="Choose a guard…"
                disabled={disabled}
                error={errors?.[`guards.${index}.key`] || ""}
                paramErrors={paramErrorsFor(index)}
                required
              />
            </li>
          ))}
        </ol>
      )}

      <SecondaryButton size="sm" onClick={addGuard} disabled={disabled} leftIcon={<PlusIcon size="sm" />}>
        Add guard
      </SecondaryButton>
    </div>
  );
};

GuardsEditor.propTypes = {
  guards: PropTypes.array,
  conditions: PropTypes.array,
  automationSlugs: PropTypes.array,
  extraSlugOptions: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** field → message, keys like "guards.0.key" / "guards.0.params.days" */
  errors: PropTypes.object,
};

export default GuardsEditor;
